import { useState, useEffect } from 'react';
import {
	Box,
	Button,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Paper,
	Typography,
	Alert,
	CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import { useCreateAddressMutation, useUpdateAddressMutation, useGetAddressesListQuery } from '@/store/services/address';
import useGetCurrentPlan from '../../billing/utilities/hooks/useGetCurrentPlan';

type Address = {
	id: number;
	user_id: number;
	name: string | null;
	location_name: string | null;
	type: string | null;
	user: {
		id: number;
		name: string;
		email: string;
	} | null;
	documents: unknown[];
	application: unknown | null;
};

type AddressFormProps = {
	editingAddress: Address | null;
	onReset: () => void;
};

function AddressForm({ editingAddress, onReset }: AddressFormProps) {
	const [name, setName] = useState('');
	const [locationName, setLocationName] = useState('');
	const [type, setType] = useState('');
	const [isFormDisabled, setIsFormDisabled] = useState(false);

	const [createAddress, { isLoading: isCreating }] = useCreateAddressMutation();
	const [updateAddress, { isLoading: isUpdating }] = useUpdateAddressMutation();
	const { data: addressesResponse } = useGetAddressesListQuery({});
	const { currentPlanInfo, isLoading: isLoadingPlanInfo } = useGetCurrentPlan();

	const isLoading = isCreating || isUpdating;

	const addresses = addressesResponse?.data || [];

	// Check plan limits based on addresses length
	// Form should be enabled when editing, disabled only when creating new address and limit is reached
	useEffect(() => {
		// Always enable form when editing
		if (editingAddress) {
			setIsFormDisabled(false);
			return;
		}

		// Disable only when creating new address and limit is reached
		if (currentPlanInfo) {
			const isLimitReached = addresses.length >= currentPlanInfo.number_of_applications;
			setIsFormDisabled(isLimitReached);
		} else {
			setIsFormDisabled(false);
		}
	}, [addresses.length, currentPlanInfo, editingAddress]);

	useEffect(() => {
		if (editingAddress) {
			setName(editingAddress.name || '');
			setLocationName(editingAddress.location_name || '');
			setType(editingAddress.type || '');
		} else {
			resetForm();
		}
	}, [editingAddress]);

	const resetForm = () => {
		setName('');
		setLocationName('');
		setType('');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!name.trim() || !locationName.trim() || !type) {
			toast.error('Please fill in all fields');
			return;
		}

		try {
			if (editingAddress) {
				// Update address
				const payload = {
					addresses: [
						{
							id: editingAddress.id,
							name: name.trim(),
							location_name: locationName.trim(),
							type: type
						}
					]
				};

				const response = await updateAddress({ payload });

				if (!response.error) {
					toast.success('Address updated successfully');
					resetForm();
					onReset();
				} else {
					toast.error(
						(response.error as { data?: { errors?: { detail?: string }[] } })?.data?.errors?.[0]?.detail ||
							'Failed to update address'
					);
				}
			} else {
				// Create address
				const payload = {
					addresses: [
						{
							name: name.trim(),
							location_name: locationName.trim(),
							type: type
						}
					]
				};

				const response = await createAddress({ payload });

				if (!response.error) {
					toast.success('Address created successfully');
					resetForm();
				} else {
					toast.error(
						(response.error as { data?: { errors?: { detail?: string }[] } })?.data?.errors?.[0]?.detail ||
							'Failed to create address'
					);
				}
			}
		} catch (error) {
			toast.error('An error occurred. Please try again.');
			console.error(error);
		}
	};

	const handleCancel = () => {
		resetForm();
		onReset();
	};

	const showLimitAlert =
		!isLoadingPlanInfo &&
		currentPlanInfo &&
		addresses.length >= currentPlanInfo.number_of_applications;

	return (
		<>
			{showLimitAlert && (
				<Alert
					severity="warning"
					className="mb-6"
				>
					You have reached the maximum number of addresses allowed in your current plan (
					{currentPlanInfo?.number_of_applications}). One application is allowed per address.
				</Alert>
			)}
			<Paper
				className="mb-6 p-6"
				elevation={1}
			>
				<Typography
					variant="h6"
					className="text-2xl text-gray-900 capitalize mb-4"
					fontWeight="700"
				>
					{editingAddress ? 'Edit Address' : 'Add New Address'}
				</Typography>
				<form onSubmit={handleSubmit}>
					<Box className="flex flex-col gap-4">
						{/* Name and Address in a row - half/half */}
						<Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<TextField
								label="Name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								fullWidth
								required
								disabled={isLoading || isFormDisabled}
								error={!name.trim() && name !== ''}
								helperText={!name.trim() && name !== '' ? 'Name is required' : ''}
							/>
							<TextField
								label="Address"
								value={locationName}
								onChange={(e) => setLocationName(e.target.value)}
								fullWidth
								required
								disabled={isLoading || isFormDisabled}
								error={!locationName.trim() && locationName !== ''}
								helperText={!locationName.trim() && locationName !== '' ? 'Address is required' : ''}
							/>
						</Box>
						{/* Type - full width */}
						<FormControl
							fullWidth
							required
							disabled={isLoading || isFormDisabled}
							error={!type && type !== ''}
						>
							<InputLabel>Type</InputLabel>
							<Select
								value={type}
								onChange={(e) => setType(e.target.value)}
								label="Type"
								disabled={isLoading || isFormDisabled}
							>
								<MenuItem value="apartment">Apartment</MenuItem>
								<MenuItem value="house">House</MenuItem>
								<MenuItem value="office">Office</MenuItem>
								<MenuItem value="residential">Residential</MenuItem>
							</Select>
						</FormControl>
					</Box>
					<Box className="flex justify-end gap-2 mt-4">
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							disabled={isLoading || isFormDisabled}
							startIcon={isLoading && <CircularProgress size={15} />}
						>
							{editingAddress ? 'Update Address' : 'Add Address'}
						</Button>
						{editingAddress && (
							<Button
								variant="outlined"
								onClick={handleCancel}
								disabled={isLoading || isFormDisabled}
							>
								Cancel
							</Button>
						)}
					</Box>
				</form>
			</Paper>
		</>
	);
}

export default AddressForm;
